import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Paper, Chip, CircularProgress,
  TextField, FormControl, Select, MenuItem, RadioGroup, FormControlLabel, Radio, IconButton
} from "@mui/material";
import { COLORS, TYPOGRAPHY, FONT_FAMILY } from "../../../constants/color";
import { Icon } from "../../../icons";
import axios from "axios";
import { url } from "../../../constants/url";
import { useAuth } from "../../../context/authContext";
import AppToast from "../../../components/AppToast";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";

const FIRRequestDetailsModal = ({ open, onClose, request, onApproveSuccess, initialEditMode = false }) => {
  const { token, hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [toastConfig, setToastConfig] = useState({ open: false, type: "success", title: "", message: "" });
  const [pdfPreviewModal, setPdfPreviewModal] = useState({ open: false, url: "", title: "" });

  const handlePreviewPdf = (fileUrl, title) => {
    setPdfPreviewModal({ open: true, url: fileUrl, title });
  };
  
  const [editFormData, setEditFormData] = useState({});

  const parseBooleanString = (val, defaultVal = "true") => {
    if (val === true || val === "true" || val === "Yes" || val === "yes") return "true";
    if (val === false || val === "false" || val === "No" || val === "no") return "false";
    return defaultVal;
  };

  useEffect(() => {
    if (request && open) {
      const firData = request.firDetails || request;
      setEditFormData({
        propertyType: firData.propertyType || request.propertyType || "",
        propertyName: firData.propertyName || request.projectName || "",
        buildingName: firData.buildingName || "",
        wing: firData.wing || "",
        floor: firData.floor || "",
        unitNumber: firData.unitNumber || "",
        fullAddress: firData.fullAddress || request.address || "",
        city: firData.city || "Pune",
        state: firData.state || "",
        pincode: firData.pincode || "",
        fullName: firData.fullName || request.clientName || "",
        mobileNumber: firData.mobileNumber || request.contactNumber || "",
        emailAddress: firData.emailAddress || "",
        altContactNumber: firData.altContactNumber || "",
        inspectionDateFrom: firData.inspectionDateFrom ? new Date(firData.inspectionDateFrom).toISOString().split('T')[0] : "",
        inspectionDateTo: firData.inspectionDateTo ? new Date(firData.inspectionDateTo).toISOString().split('T')[0] : "",
        preferredTimeSlot: firData.preferredTimeSlot || "",
        propertyStatus: firData.propertyStatus || "",
        purposeOfInspection: firData.purposeOfInspection || "",
        builderName: firData.builderName || "",
        projectName: firData.projectName || "",
        towerPhase: firData.towerPhase || "",
        reraNumber: firData.reraNumber || "",
        contactPersonName: firData.contactPersonName || "",
        contactPersonMobile: firData.contactPersonMobile || "",
        accessAvailable: parseBooleanString(firData.accessAvailable ?? firData.siteAccessAvailable ?? request.accessAvailable, "true"),
        securityGatePass: parseBooleanString(firData.securityGatePass ?? firData.securityGatePassRequired ?? request.securityGatePass, "true"),
        accessInstructions: firData.accessInstructions || "",
        landmark: firData.landmark || "",
        specialInstructions: firData.specialInstructions || "",
        areasOfConcern: firData.areasOfConcern || "",
        documents: firData.documents || [],
      });
      setIsEditing(initialEditMode);
    }
  }, [request, open]);

  if (!request) return null;

  const showToast = (type, title, message) => setToastConfig({ open: true, type, title, message });

  const handleFieldChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const firId = (request.firDetails?._id) || request._id;
    try {
      setSaving(true);
      const res = await axios.put(`${url}/api/fir/requests/update/${firId}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        showToast("success", "Success", "FIR Request updated successfully.");
        setIsEditing(false);
        if (onApproveSuccess) onApproveSuccess();
      }
    } catch (error) {
      showToast("error", "Error", error.response?.data?.message || "Failed to update FIR request.");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${url}/api/fir/requests/${request._id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        showToast("success", "Success", "FIR Request approved and mapped to project.");
        setTimeout(() => {
          onApproveSuccess();
          onClose();
        }, 1500);
      }
    } catch (error) {
      showToast("error", "Error", error.response?.data?.message || "Failed to approve request.");
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title }) => (
    <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontWeight: 700, mb: 3, mt: 1, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}60`, pb: 1 }}>
      {title}
    </Typography>
  );

  const InfoItem = ({ label, value }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</Typography>
      <Typography sx={{ color: COLORS.TEXT, fontSize: "15px", fontWeight: 500, wordBreak: "break-word" }}>{value || "-"}</Typography>
    </Box>
  );

  const defaultTimeSlots = ["10:00 AM - 01:00 PM", "02:00 PM - 05:00 PM", "10:00 AM - 05:00 PM", "09:00 AM - 12:00 PM", "01:00 PM - 04:00 PM"];
  const timeSlotOptions = editFormData.preferredTimeSlot && !defaultTimeSlots.includes(editFormData.preferredTimeSlot)
    ? [editFormData.preferredTimeSlot, ...defaultTimeSlots]
    : defaultTimeSlots;

  const EditItem = ({ label, field, type = "text", options = null, multiline = false, rows = 2 }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ color: COLORS.PRIMARY, fontSize: "12px", fontWeight: 600 }}>{label}</Typography>
      {options ? (
        <FormControl fullWidth size="small">
          <Select value={editFormData[field] || ""} onChange={(e) => handleFieldChange(field, e.target.value)}>
            {options.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : type === "date" ? (
        <TextField
          fullWidth
          size="small"
          type="date"
          value={editFormData[field] || ""}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          onClick={(e) => e.target.showPicker && e.target.showPicker()}
        />
      ) : (
        <TextField fullWidth size="small" multiline={multiline} rows={multiline ? rows : 1} value={editFormData[field] || ""} onChange={(e) => handleFieldChange(field, e.target.value)} />
      )}
    </Box>
  );

  const gridStyle = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: "24px 16px" };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: 'hidden', boxShadow: "0 10px 40px rgba(0,0,0,0.1)" } }}>
      <DialogTitle sx={{ backgroundColor: COLORS.PRIMARY, color: "white", py: 2.5, px: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={{ ...TYPOGRAPHY.H3, m: 0, fontWeight: 600 }}>FIR Request Details</Typography>
          <Box display="flex" alignItems="center" gap={1.5}>
            {(() => {
              const hasReport = !!request.hasReport || (request.reportCount && request.reportCount > 0);
              const isCompleted = hasReport || request.firStatus === "Completed Inspection" || request.firStatus === "Report Submitted" || request.projectStatus === "completed" || request.status === "FINALREPORT";
              const displayStatus = isCompleted ? "Report Submitted" : (editFormData.firStatus || request.firStatus || "Pending");
              return (
                <Chip label={displayStatus} size="small" sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 600, border: "1px solid rgba(255,255,255,0.3)" }} />
              );
            })()}
            {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && !isEditing && (
              <Button size="small" variant="contained" startIcon={<Icon name="edit" size={16} color="white" />} onClick={() => setIsEditing(true)} sx={{ backgroundColor: COLORS.SECONDARY, color: "white", textTransform: "none", fontWeight: 600 }}>
                Edit Details
              </Button>
            )}
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 4, backgroundColor: "#f8f9fa", display: 'flex', flexDirection: 'column', gap: 3 }}>
        
        {/* 1. Property Details */}
        <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
          <SectionTitle title="1. Property Details" />
          <Box sx={gridStyle}>
            {isEditing ? (
              <>
                <EditItem label="Property Type" field="propertyType" />
                <EditItem label="Property Name" field="propertyName" />
                <EditItem label="Building / Society Name" field="buildingName" />
                <EditItem label="Wing / Tower" field="wing" />
                <EditItem label="Floor" field="floor" />
                <EditItem label="Unit / Flat No" field="unitNumber" />
                <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 3' } }}>
                  <EditItem label="Full Address" field="fullAddress" multiline rows={2} />
                </Box>
                <EditItem label="City" field="city" />
                <EditItem label="State" field="state" />
                <EditItem label="PIN Code" field="pincode" />
              </>
            ) : (
              <>
                <InfoItem label="Property Type" value={editFormData.propertyType} />
                <InfoItem label="Property Name" value={editFormData.propertyName} />
                <InfoItem label="Building/Society Name" value={editFormData.buildingName} />
                <InfoItem label="Wing/Tower" value={editFormData.wing} />
                <InfoItem label="Floor" value={editFormData.floor} />
                <InfoItem label="Unit/Flat No" value={editFormData.unitNumber} />
                <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 3' } }}>
                  <InfoItem label="Full Address" value={editFormData.fullAddress} />
                </Box>
                <InfoItem label="City & State" value={`${editFormData.city || "Pune"}, ${editFormData.state} - ${editFormData.pincode}`} />
              </>
            )}
          </Box>
        </Paper>

        {/* 2. Client Details */}
        <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
          <SectionTitle title="2. Client Details" />
          <Box sx={gridStyle}>
            {isEditing ? (
              <>
                <EditItem label="Full Name" field="fullName" />
                <EditItem label="Mobile Number" field="mobileNumber" />
                <EditItem label="Email Address" field="emailAddress" />
                <EditItem label="Alternate Contact" field="altContactNumber" />
              </>
            ) : (
              <>
                <InfoItem label="Full Name" value={editFormData.fullName} />
                <InfoItem label="Mobile Number" value={editFormData.mobileNumber} />
                <InfoItem label="Email Address" value={editFormData.emailAddress} />
                <InfoItem label="Alternate Contact" value={editFormData.altContactNumber} />
              </>
            )}
          </Box>
        </Paper>

        {/* 3. Inspection Details */}
        <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
          <SectionTitle title="3. Inspection Details" />
          <Box sx={gridStyle}>
            {isEditing ? (
              <>
                <EditItem label="Preferred Date From" field="inspectionDateFrom" type="date" />
                <EditItem label="Preferred Date To" field="inspectionDateTo" type="date" />
                <EditItem label="Preferred Time Slot" field="preferredTimeSlot" options={timeSlotOptions} />
                <EditItem label="Property Status" field="propertyStatus" options={["Under Construction", "Ready Possession", "Renovation", "Occupied"]} />
                <EditItem label="Purpose of Inspection" field="purposeOfInspection" options={["Before Possession", "During Construction", "Quality Check", "Snag Inspection", "Other"]} />
              </>
            ) : (
              <>
                <InfoItem 
                  label="Preferred Dates" 
                  value={editFormData.inspectionDateFrom ? `${new Date(editFormData.inspectionDateFrom).toLocaleDateString()} to ${editFormData.inspectionDateTo ? new Date(editFormData.inspectionDateTo).toLocaleDateString() : "-"}` : "-"} 
                />
                <InfoItem label="Preferred Time Slot" value={editFormData.preferredTimeSlot} />
                <InfoItem label="Property Status" value={editFormData.propertyStatus} />
                <InfoItem label="Purpose of Inspection" value={editFormData.purposeOfInspection} />
              </>
            )}
          </Box>
        </Paper>

        {/* 4. Builder / Project Info */}
        <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
          <SectionTitle title="4. Builder / Project Information" />
          <Box sx={gridStyle}>
            {isEditing ? (
              <>
                <EditItem label="Builder Name" field="builderName" />
                <EditItem label="Project Name" field="projectName" />
                <EditItem label="Tower / Phase" field="towerPhase" />
                <EditItem label="RERA Number" field="reraNumber" />
              </>
            ) : (
              <>
                <InfoItem label="Builder Name" value={editFormData.builderName} />
                <InfoItem label="Project Name" value={editFormData.projectName} />
                <InfoItem label="Tower / Phase" value={editFormData.towerPhase} />
                <InfoItem label="RERA Number" value={editFormData.reraNumber} />
              </>
            )}
          </Box>
        </Paper>

        {/* 5. Site Contact Person */}
        <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
          <SectionTitle title="5. Site Contact Person" />
          <Box sx={gridStyle}>
            {isEditing ? (
              <>
                <EditItem label="Contact Person Name" field="contactPersonName" />
                <EditItem label="Mobile Number" field="contactPersonMobile" />
              </>
            ) : (
              <>
                <InfoItem label="Contact Person Name" value={editFormData.contactPersonName} />
                <InfoItem label="Mobile Number" value={editFormData.contactPersonMobile} />
              </>
            )}
          </Box>
        </Paper>

        {/* 6. Property Access */}
        <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
          <SectionTitle title="6. Property Access" />
          <Box sx={gridStyle}>
            {isEditing ? (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography sx={{ color: COLORS.PRIMARY, fontSize: "12px", fontWeight: 600 }}>Site Access Available</Typography>
                  <RadioGroup row value={editFormData.accessAvailable ?? ""} onChange={(e) => handleFieldChange("accessAvailable", e.target.value)}>
                    <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                  </RadioGroup>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography sx={{ color: COLORS.PRIMARY, fontSize: "12px", fontWeight: 600 }}>Security Gate Pass Required</Typography>
                  <RadioGroup row value={editFormData.securityGatePass ?? ""} onChange={(e) => handleFieldChange("securityGatePass", e.target.value)}>
                    <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                  </RadioGroup>
                </Box>
                <EditItem label="Access Instructions" field="accessInstructions" />
                <EditItem label="Landmark" field="landmark" />
              </>
            ) : (
              <>
                <InfoItem label="Access Available" value={editFormData.accessAvailable === "true" || editFormData.accessAvailable === true ? "Yes" : "No"} />
                <InfoItem label="Security Gate Pass" value={editFormData.securityGatePass === "true" || editFormData.securityGatePass === true ? "Yes" : "No"} />
                <InfoItem label="Access Instructions" value={editFormData.accessInstructions} />
                <InfoItem label="Landmark" value={editFormData.landmark} />
              </>
            )}
          </Box>
        </Paper>

        {/* 7. Additional Info */}
        <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
          <SectionTitle title="7. Additional Information" />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
            {isEditing ? (
              <>
                <EditItem label="Special Instructions" field="specialInstructions" />
                <EditItem label="Areas of Concern" field="areasOfConcern" />
              </>
            ) : (
              <>
                <InfoItem label="Special Instructions" value={editFormData.specialInstructions} />
                <InfoItem label="Areas of Concern" value={editFormData.areasOfConcern} />
              </>
            )}
          </Box>
        </Paper>

        {editFormData.documents?.length > 0 && (
          <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
            <SectionTitle title="8. Uploaded Documents & PDF Preview" />
            <Box display="flex" gap={2} flexWrap="wrap">
              {editFormData.documents.map((doc, idx) => {
                const docUrl = doc.url?.startsWith("http") ? doc.url : `${url}${doc.url}`;
                return (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      p: 2,
                      minWidth: "220px",
                      borderRadius: "10px",
                      border: `1px solid ${COLORS.LIGHT_SECONDARY}40`,
                      backgroundColor: "#F9F9FB",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <PictureAsPdfIcon sx={{ color: "error.main", fontSize: 28 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: COLORS.PRIMARY,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {doc.documentType || `Document ${idx + 1}`}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: COLORS.MUTED_TEXT,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {doc.name || "Uploaded PDF"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={() => handlePreviewPdf(docUrl, doc.documentType || doc.name)}
                        sx={{
                          backgroundColor: COLORS.PRIMARY,
                          color: "#fff",
                          fontSize: "11px",
                          fontWeight: 700,
                          textTransform: "none",
                          borderRadius: "6px",
                          "&:hover": { backgroundColor: COLORS.SECONDARY },
                        }}
                      >
                        Preview
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        component="a"
                        href={docUrl}
                        target="_blank"
                        download
                        sx={{
                          borderColor: `${COLORS.LIGHT_SECONDARY}40`,
                          color: COLORS.PRIMARY,
                          minWidth: "36px",
                          p: 0.5,
                        }}
                      >
                        <DownloadIcon fontSize="small" />
                      </Button>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}40` }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderColor: COLORS.GREY_COLOR, color: COLORS.TEXT }}>Close</Button>
        {isEditing ? (
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ backgroundColor: COLORS.PRIMARY, color: "white" }}>
            {saving ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
          </Button>
        ) : (
          request.firStatus === "Requested to Admin" && (
            <Button onClick={handleApprove} variant="contained" disabled={loading} sx={{ backgroundColor: COLORS.PRIMARY, color: "white" }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : "Approve & Create Project"}
            </Button>
          )
        )}
      </DialogActions>

      {/* PDF Preview Dialog inside Details Modal */}
      <Dialog
        open={pdfPreviewModal.open}
        onClose={() => setPdfPreviewModal({ open: false, url: "", title: "" })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", height: "80vh" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontFamily: FONT_FAMILY,
            color: COLORS.PRIMARY,
            fontWeight: 700,
            borderBottom: `1px solid ${COLORS.BORDER || "#E0E0E0"}`,
            py: 1.5,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <PictureAsPdfIcon sx={{ color: "error.main" }} />
            <Typography variant="h6" fontWeight={700}>
              {pdfPreviewModal.title || "PDF Document Preview"}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {pdfPreviewModal.url && (
              <Button
                size="small"
                variant="outlined"
                href={pdfPreviewModal.url}
                target="_blank"
                sx={{ textTransform: "none", fontSize: "12px" }}
              >
                Open in New Tab
              </Button>
            )}
            <IconButton
              onClick={() => setPdfPreviewModal({ open: false, url: "", title: "" })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "100%", backgroundColor: "#525659" }}>
          {pdfPreviewModal.url ? (
            <iframe
              src={pdfPreviewModal.url}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="white">No document preview available.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <AppToast open={toastConfig.open} onClose={() => setToastConfig(p => ({...p, open: false}))} type={toastConfig.type} title={toastConfig.title} message={toastConfig.message} />
    </Dialog>
  );
};

export default FIRRequestDetailsModal;
